<template>
	<div
		class="absolute inset-0 z-8 flex flex-col items-center justify-center py-8 transition-opacity duration-500"
		:class="{ 'pointer-events-none opacity-0': !localShow, 'bg-black': !noBg }"
	>
		<LazySticker
			v-if="winnerItems[0]"
			:src="animation(winnerItems[0].modelUuid)"
			:forceSize="72"
			autoplay
			class="transition-all duration-700"
			:class="{
				'animate-hover-1 !absolute -top-6 left-1/2 h-18 w-18 -translate-x-1/2 rotate-[-12deg]':
					participants.length < 4,
				'animate-hover-1 !absolute top-22 left-1/2 h-23 w-23 -translate-x-1/2 rotate-[-12deg]':
					participants.length >= 4,
				'translate-y-1/2 opacity-0': !localShow
			}"
			:preloader="winnerItems[0].preloader || undefined"
		/>
		<LazySticker
			v-if="winnerItems[1]"
			:src="animation(winnerItems[1].modelUuid)"
			:forceSize="72"
			autoplay
			class="transition-all duration-700"
			:class="{
				'animate-hover-2 !absolute top-10 -left-5 h-18 w-18 rotate-[-12deg]':
					participants.length < 4,
				'animate-hover-2 !absolute top-30 left-[11%] h-18 w-18 rotate-[-12deg]':
					participants.length >= 4,
				'translate-x-1/2 translate-y-1/2 opacity-0': !localShow
			}"
			:preloader="winnerItems[1].preloader || undefined"
		/>
		<LazySticker
			v-if="winnerItems[2]"
			:src="animation(winnerItems[2].modelUuid)"
			:forceSize="72"
			autoplay
			class="transition-all duration-700"
			:class="{
				'animate-hover-3 !absolute top-17 -right-7 h-18 w-18 rotate-[18deg]':
					participants.length < 4,
				'animate-hover-3 !absolute top-30 right-[11%] h-18 w-18 rotate-[18deg]':
					participants.length >= 4,
				'-translate-x-1/2 translate-y-1/2 opacity-0': !localShow
			}"
			:preloader="winnerItems[2].preloader || undefined"
		/>
		<Transition
			enterActiveClass="transition-all duration-800"
			enterFromClass="opacity-0 translate-y-full"
			leaveActiveClass="transition-all duration-800"
			leaveToClass="opacity-0 translate-y-full"
		>
			<div
				class="animate-fade-in-y-1 font-medium uppercase"
				:class="{
					'mt-9': participants.length < 4 && winnerItems.length,
					'mt-auto': participants.length >= 4 && winnerItems.length,
					'text-label-secondary text-xs': !noBg,
					'text-label-primary text-lg': noBg
				}"
				v-if="localShow"
			>
				{{ winnerPlayersText }}
			</div>
		</Transition>
		<Transition
			enterActiveClass="transition-all duration-900"
			enterFromClass="opacity-0 translate-y-1/2"
			leaveActiveClass="transition-all duration-900"
			leaveToClass="opacity-0 translate-y-1/2"
		>
			<CoinPrice
				v-if="localShow"
				class="animate-fade-in-y-2 mt-2"
				:class="{ 'animate-blink': blink }"
				size="lg"
				:price="amount"
				:minDigits="2"
				mono
			/>
		</Transition>
		<Transition
			enterActiveClass="transition-all duration-1000"
			enterFromClass="opacity-0 translate-y-1/2"
			leaveActiveClass="transition-all duration-1000"
			leaveToClass="opacity-0 translate-y-1/2"
		>
			<BaseButton
				v-if="localShow && !noBg"
				class="animate-fade-in-y-3 mt-6"
				size="md"
				color="primary"
				:loading="loading"
				@click="recreate()"
			>
				<div>{{ t('reCreate') }}</div>
				<CoinPrice :price="price" />
			</BaseButton>
		</Transition>
		<Transition
			enterActiveClass="transition-all duration-1100"
			enterFromClass="opacity-0 translate-y-1/2"
			leaveActiveClass="transition-all duration-1100"
			leaveToClass="opacity-0 translate-y-1/2"
		>
			<div
				v-if="localShow && !noBg"
				class="text-label-tertiary mt-2 flex items-center gap-1 px-3 py-2 text-sm uppercase"
				@click="watchAgain"
				v-hover="{ active: 'scale-95' }"
			>
				{{ t('watch') }}
			</div>
		</Transition>
	</div>
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import CoinPrice from './CoinPrice.vue'
import BaseButton from './BaseButton.vue'
import { sleep } from '@/utils/sleep'
import LazySticker from './LazySticker.vue'
import { animation } from '@/utils/urls'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	messages: {
		en: {
			reCreate: 'Re-create battle',
			watch: 'Watch again',
			won: 'won',
			and: '&',
			winners: '{0} players'
		},
		ru: {
			reCreate: 'Создать заново',
			watch: 'Посмотреть снова',
			won: 'выиграл | выиграл | выиграли | выиграли | выиграли',
			and: 'и',
			winners: '{0} игрока'
		}
	}
})

const props = defineProps<{
	participants: {
		name: string | null
		teamIndex: number
		winner: {
			wonCoins: number
		} | null
	}[]
	rounds: {
		results: {
			item?: {
				modelUuid: string
				price: number
				preloader: string | null
			}
		}[]
	}[]
	wonCoins: number
	price: number
	show?: boolean
	noBg?: boolean
	duration?: number
}>()

const emit = defineEmits<{
	(e: 'recreate'): void
	(e: 'watchAgain'): void
}>()

const winnerPlayersText = computed(() => {
	const winners = props.participants.filter(
		(participant) => participant.winner?.wonCoins
	)

	if (winners.length > 2) {
		return `${t('winners', [winners.length])} ${t('won', winners.length)}`
	}

	return `${winners.map((participant) => participant.name).join(` ${t('and')} `)} ${t('won', winners.length)}`
})

const loading = ref(false)

const recreate = () => {
	loading.value = true

	emit('recreate')
}

const watchAgain = () => {
	emit('watchAgain')
}

const amount = ref(0)
const blink = ref(false)

let start = 0
const createAnimationFrame = (from: number, to: number, duration: number) => {
	start = performance.now()
	const animate = (current: number) => {
		const progress = (current - start) / duration
		amount.value = from + (to - from) * progress * (2 - progress)
		if (progress < 1) {
			requestAnimationFrame(animate)
		} else {
			amount.value = to
		}
	}
	requestAnimationFrame(animate)
}

const winnerItems = computed(() => {
	const allItems = props.rounds
		.reduce(
			(a, round) => [...a, ...round.results.map((result) => result.item)],
			[] as (
				| {
						modelUuid: string
						price: number
						preloader: string | null
				  }
				| undefined
			)[]
		)
		.reduce(
			(a, item) => {
				if (!item) {
					return a
				}

				a[item.modelUuid] = { price: item.price, preloader: item.preloader }

				return a
			},
			{} as Record<string, { price: number; preloader: string | null }>
		)

	const expensiveItems: {
		modelUuid: string
		price: number
		preloader: string | null
	}[] = []

	for (const modelUuid in allItems) {
		expensiveItems.push({
			modelUuid,
			price: allItems[modelUuid].price,
			preloader: allItems[modelUuid].preloader
		})
	}

	expensiveItems.sort((a, b) => {
		if (a.price > b.price) {
			return -1
		}
		if (a.price < b.price) {
			return 1
		}
		return 0
	})

	const result = expensiveItems.slice(0, 3)

	for (let i = result.length; i < 3; i++) {
		const lastItem = result[result.length - 1]

		if (!lastItem) {
			break
		}

		result.push(lastItem)
	}

	return result
})

const localShow = ref(false)

const runTheShow = async () => {
	await nextTick()

	localShow.value = props.show

	if (!props.show) {
		blink.value = false
		return
	}

	const longDuration = props.duration ? props.duration : 3500

	const from = 0
	const to = props.wonCoins
	createAnimationFrame(from, to, winnerItems.value.length ? longDuration : 2500)
	await sleep(winnerItems.value.length ? longDuration - 50 : 2450)
	blink.value = true
}

watch(() => props.show, runTheShow)

onMounted(runTheShow)
</script>
